// "Scan & advise" for one sandbox host (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md
// §16.6). The API only queues the scan; a worker measures the host (CPUs,
// memory, Docker's disk, other containers' limits) and stores the result, and
// the API computes a slot advice from it. So the panel polls the host until
// `lastScanAt` changes or the queue's 5 minutes are up.
//
// The advice never changes the host by itself: "Apply advice" fills the form's
// slot fields and the administrator saves.

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SandboxHost, SandboxHostAdvice } from '@goblic/shared'
import { ApiError, sandboxHostsApi } from '../lib/api-client'
import { formatRelative } from '../lib/format'
import {
  SCAN_POLL_LIMIT_MS,
  SCAN_POLL_MS,
  adviceForSlots,
  adviceHeadline,
  bytesText,
  diskText,
  foreignText,
  ownText,
  scanFinished,
} from '../lib/sandbox-host-scan'
import { Notice } from './ui'

/** A scan in flight: the `lastScanAt` seen when it was asked for, and when. */
interface Waiting {
  before: string | null
  since: number
}

export function SandboxHostScanPanel({
  hostId,
  initial,
  autoStart = false,
  slots,
  onApply,
}: {
  hostId: string
  initial: SandboxHost
  autoStart?: boolean
  /** The slots the form holds now; the "slots are set to …" warnings compare against these. */
  slots?: { heavySlots: number | null; lightSlots: number | null }
  onApply?: (advice: SandboxHostAdvice) => void
}) {
  const queryClient = useQueryClient()
  const [latest, setLatest] = useState(initial)
  const [waiting, setWaiting] = useState<Waiting | null>(null)
  const [timedOut, setTimedOut] = useState(false)

  const scan = useMutation({
    mutationFn: () => sandboxHostsApi.scan(hostId),
    onMutate: () => {
      setTimedOut(false)
      setWaiting({ before: latest.lastScanAt, since: Date.now() })
    },
    onError: () => setWaiting(null),
  })

  const poll = useQuery({
    queryKey: ['sandbox-host-scan', hostId, waiting?.since ?? 0],
    queryFn: () => sandboxHostsApi.get(hostId),
    enabled: waiting != null && scan.isSuccess,
    refetchInterval: SCAN_POLL_MS,
    gcTime: 0,
  })

  useEffect(() => {
    if (!waiting || !poll.data || !scanFinished(waiting.before, poll.data)) return
    setLatest(poll.data)
    setWaiting(null)
    // The list shows the advice under Capacity too.
    void queryClient.invalidateQueries({ queryKey: ['sandbox-hosts'], exact: true })
  }, [poll.data, waiting, queryClient])

  useEffect(() => {
    if (!waiting) return
    const timer = setTimeout(() => {
      setWaiting(null)
      setTimedOut(true)
    }, Math.max(0, SCAN_POLL_LIMIT_MS - (Date.now() - waiting.since)))
    return () => clearTimeout(timer)
  }, [waiting])

  // Once per mount, also under StrictMode's double effect.
  const autoStarted = useRef(false)
  useEffect(() => {
    if (!autoStart || autoStarted.current) return
    autoStarted.current = true
    scan.mutate()
  }, [autoStart, scan])

  const result = latest.lastScan
  const advice = adviceForSlots(latest, slots)
  const busy = waiting != null

  return (
    <section className="space-y-3 rounded-control border border-line px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-ink">Scan & advise</h3>
          <p className="text-sm text-ink-3">
            Measures this host and suggests slots. Nothing changes until you save.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm shrink-0"
          onClick={() => scan.mutate()}
          disabled={busy}
        >
          {busy ? 'Scanning…' : 'Scan & advise'}
        </button>
      </div>

      {busy ? (
        <p className="text-sm text-ink-3" role="status">
          Scanning… The first scan of a new host also builds a small helper image (1–2 min).
        </p>
      ) : null}
      {scan.isError ? (
        <Notice tone="error">
          {scan.error instanceof ApiError ? scan.error.message : 'Couldn’t start the scan.'}
        </Notice>
      ) : null}
      {timedOut ? <Notice tone="error">No worker answered. Is a worker running?</Notice> : null}

      {!result ? (
        busy ? null : <p className="text-sm text-ink-3">Not scanned yet.</p>
      ) : !result.ok ? (
        <Notice tone="error">
          {result.error ?? 'The scan failed.'}
          <span className="block text-sm text-ink-3">
            scanned {formatRelative(latest.lastScanAt)}
          </span>
        </Notice>
      ) : (
        <>
          <ScanSpecs host={latest} />
          {advice ? (
            <div className="space-y-2">
              <h4 className="text-base font-medium text-ink">Advice: {adviceHeadline(advice)}</h4>
              <ul className="list-disc space-y-1 pl-5 text-sm text-ink-2">
                {advice.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              {advice.warnings.length > 0 ? (
                <Notice tone="signal">
                  <ul className="space-y-1">
                    {advice.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Notice>
              ) : null}
              {onApply ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => onApply(advice)}
                    disabled={busy}
                  >
                    Apply advice
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-ink-3">No advice: the scan has no CPU or memory figures.</p>
          )}
        </>
      )}
    </section>
  )
}

/** What the last successful scan measured. */
function ScanSpecs({ host }: { host: SandboxHost }) {
  const scan = host.lastScan
  if (!scan) return null
  const docker = scan.docker
  const rows: [string, string][] = [
    ['CPUs', docker?.cpus != null ? String(docker.cpus) : '–'],
    ['Memory', bytesText(docker?.memoryBytes)],
    ['Disk', diskText(scan) ?? `Not measured${scan.diskError ? `: ${scan.diskError}` : ''}`],
    ['Architecture', docker?.architecture ?? '–'],
    ['OS', docker?.operatingSystem ?? '–'],
    ['Docker', docker?.serverVersion ?? '–'],
    ['Other containers', scan.foreign ? foreignText(scan.foreign) : '–'],
    ['Goblic sandboxes', scan.own ? ownText(scan.own) : '–'],
  ]
  return (
    <div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-3">{label}</dt>
            <dd className="text-ink">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-1 text-sm text-ink-3">
        Scanned {formatRelative(host.lastScanAt)} by {scan.scannedBy}.
      </p>
    </div>
  )
}
